// @ts-nocheck
/**
 * PromptLab - 提示词管理组件
 *
 * 功能：
 * - 查看和编辑系统提示词
 * - 变量预览
 * - 版本历史和回滚
 */

import React, { useState, useEffect } from 'react';
import { apiGet, apiPut, apiPost } from '../../utils/apiClient';

interface PromptLabProps {
  onRefresh?: () => void;
}

const CATEGORIES = [
  { id: 'all', label: '全部' },
  { id: 'guide', label: '导读' },
  { id: 'qa', label: '问答' },
  { id: 'review', label: '审阅' },
];

export default function PromptLab({ onRefresh }: PromptLabProps) {
  const [prompts, setPrompts] = useState<any[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<any>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [changeNote, setChangeNote] = useState<string>('');
  const [versions, setVersions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [showVersions, setShowVersions] = useState<boolean>(false);
  const [previewResult, setPreviewResult] = useState<string>('');

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/api/admin/config/prompts') as any;
      if (Array.isArray(response)) {
        setPrompts(response);
      }
    } catch (error: any) {
      console.error('获取提示词列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectPrompt = async (prompt: any) => {
    try {
      const response = await apiGet(`/api/admin/config/prompts/${prompt.prompt_key}`) as any;
      if (response) {
        setSelectedPrompt(response);
        setEditContent(response.content || '');
        setChangeNote('');
        setShowVersions(false);
        fetchVersions(prompt.prompt_key);
      }
    } catch (error: any) {
      console.error('获取提示词详情失败:', error);
    }
  };

  const fetchVersions = async (promptKey: string) => {
    try {
      const response = await apiGet(`/api/admin/config/prompts/${promptKey}/versions`) as any;
      if (response.success) {
        setVersions(response.versions || []);
      }
    } catch (error: any) {
      console.error('获取版本历史失败:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedPrompt || !editContent.trim()) return;

    try {
      setSaving(true);
      const response = await apiPut(
        `/api/admin/config/prompts/${selectedPrompt.prompt_key}`,
        { content: editContent, change_note: changeNote }
      ) as any;

      if (response.success) {
        alert('保存成功');
        fetchPrompts();
        fetchVersions(selectedPrompt.prompt_key);
        setChangeNote('');
        onRefresh?.();
      } else {
        alert('保存失败: ' + response.message);
      }
    } catch (error: any) {
      console.error('保存失败:', error);
      alert('保存失败: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRollback = async (version: number) => {
    if (!window.confirm(`确定回滚到版本 v${version}?`)) return;

    try {
      const response = await apiPost(
        `/api/admin/config/prompts/${selectedPrompt.prompt_key}/rollback`,
        { target_version: version }
      ) as any;

      if (response.success) {
        alert('回滚成功');
        selectPrompt({ prompt_key: selectedPrompt.prompt_key });
        fetchPrompts();
        onRefresh?.();
      } else {
        alert('回滚失败: ' + response.message);
      }
    } catch (error: any) {
      console.error('回滚失败:', error);
      alert('回滚失败');
    }
  };

  const handlePreview = async () => {
    if (!editContent) return;

    try {
      // 模拟变量替换预览
      const sampleVariables = {
        context: '[文档内容示例...]',
        question: '[用户问题示例...]'
      };

      const response = await apiPost('/api/admin/config/prompts/preview', {
        content: editContent,
        variables: sampleVariables
      }) as any;

      if (response.success) {
        setPreviewResult(response.preview);
      }
    } catch (error: any) {
      console.error('预览失败:', error);
    }
  };

  const filteredPrompts = activeCategory === 'all'
    ? prompts
    : prompts.filter((p: any) => p.category === activeCategory);

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  return (
    <div className="prompt-lab">
      {/* 顶部筛选 */}
      <div className="admin-panel">
        <div className="panel-header">
          <h3 className="panel-title">📝 提示词管理</h3>
          <div className="category-tabs">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`category-tab ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* 提示词列表 */}
        <div className="card-grid">
          {filteredPrompts.map((prompt: any) => (
            <div
              key={prompt.prompt_key}
              className={`config-card ${selectedPrompt?.prompt_key === prompt.prompt_key ? 'selected' : ''}`}
              onClick={() => selectPrompt(prompt)}
            >
              <div className="config-card-header">
                <span className="config-card-title">{prompt.name}</span>
                <span className="badge badge-info">v{prompt.version}</span>
              </div>
              <div className="config-card-desc">{prompt.description || prompt.prompt_key}</div>
              <div className="config-card-meta">
                <span className="badge badge-success">{prompt.category}</span>
                {prompt.is_system && <span className="badge badge-warning">系统</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 编辑区域 */}
      {selectedPrompt && (
        <div className="admin-panel">
          <div className="panel-header">
            <div>
              <h3 className="panel-title">{selectedPrompt.name}</h3>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                {selectedPrompt.prompt_key}
              </span>
            </div>
            <div className="panel-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowVersions(!showVersions)}
              >
                {showVersions ? '隐藏历史' : '版本历史'}
              </button>
              <button
                className="btn-secondary"
                onClick={handlePreview}
              >
                预览效果
              </button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px' }}>
            {/* 编辑器 */}
            <div style={{ flex: 1 }}>
              <div className="code-editor">
                <div className="code-editor-header">
                  <span className="code-editor-title">提示词内容</span>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                    {editContent.length} 字符
                  </span>
                </div>
                <textarea
                  value={editContent}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditContent(e.target.value)}
                  placeholder="输入提示词内容..."
                />
              </div>

              {/* 变量说明 */}
              {selectedPrompt.variables && selectedPrompt.variables.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div className="form-label">可用变量</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {selectedPrompt.variables.map((v: any, i: number) => (
                      <code
                        key={i}
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(0,245,212,0.1)',
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: '#00f5d4'
                        }}
                      >
                        {`{${v.name || v}}`}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {/* 修改说明 */}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">修改说明（可选）</label>
                <input
                  type="text"
                  className="form-input"
                  value={changeNote}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChangeNote(e.target.value)}
                  placeholder="简要说明此次修改..."
                />
              </div>
            </div>

            {/* 版本历史侧栏 */}
            {showVersions && (
              <div style={{ width: '280px' }}>
                <div className="form-label" style={{ marginBottom: '12px' }}>版本历史</div>
                <div className="version-history">
                  {versions.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-state-text">暂无版本记录</div>
                    </div>
                  ) : (
                    versions.map((v: any) => (
                      <div key={v.version} className="version-item">
                        <span className="version-number">v{v.version}</span>
                        <div className="version-info">
                          <div className="version-note">
                            {v.change_note || '无说明'}
                          </div>
                          <div className="version-meta">
                            {v.created_by_name || '系统'} · {new Date(v.created_at).toLocaleString()}
                          </div>
                        </div>
                        <button
                          className="btn-icon"
                          onClick={() => handleRollback(v.version)}
                          title="回滚到此版本"
                        >
                          ↩️
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 预览结果 */}
          {previewResult && (
            <div style={{ marginTop: '20px' }}>
              <div className="form-label">预览结果</div>
              <div
                style={{
                  padding: '16px',
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: '8px',
                  whiteSpace: 'pre-wrap',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}
              >
                {previewResult}
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        .category-tabs {
          display: flex;
          gap: 8px;
        }
        .category-tab {
          padding: 6px 12px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 6px;
          color: rgba(255,255,255,0.6);
          cursor: pointer;
          transition: all 0.2s;
          font-size: 13px;
        }
        .category-tab:hover {
          background: rgba(255,255,255,0.1);
          color: #fff;
        }
        .category-tab.active {
          background: rgba(0,245,212,0.1);
          border-color: rgba(0,245,212,0.3);
          color: #00f5d4;
        }
        .config-card.selected {
          border-color: #00f5d4;
          background: rgba(0,245,212,0.05);
        }
      `}</style>
    </div>
  );
}
